import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useProductDetailContext } from "../../context/ProductDetailContext";

import FormInput from "@/components/form-fields/form-input";
import { FileUploaderComponent } from "@/components/reusables/FileUpload";
import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names, REQUEST_METHODS } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { ISubProductItem } from "@/interfaces/tools.interface";
import {
  useGetSingleSubProductItemQuery,
  useMutateSubProductItemMutation,
} from "@/redux/api/toolsQuery";
import {
  TUpdateSubProductItem,
  emptySubProductItems,
  updateSubProductItemSchema,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import {
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface UpdateItemModalProps {
  isOpen: boolean;
  toggleUpdateItemModal: () => void;
}

const UpdateItemModal = ({
  isOpen,
  toggleUpdateItemModal,
}: UpdateItemModalProps): JSX.Element => {
  const { productId } = useProductDetailContext();
  const { id: itemId, reset } = useQueryID(ToolsKeys.SUB_PRODUCT_ITEM_ID);
  const { id: subProductId } = useQueryID(ToolsKeys.SUB_PRODUCT_ID);

  const [updateSubProductItem, { isLoading }] =
    useMutateSubProductItemMutation();
  const { data, isLoading: isItemFetching } = useGetSingleSubProductItemQuery({
    productId,
    itemId,
    subProductId,
  });

  const singleItem = data?.data as ISubProductItem;

  const form = useForm<TUpdateSubProductItem>({
    ...(singleItem && {
      values: {
        id: singleItem.id,
        name: singleItem?.name || "",
        modelNumber: singleItem?.modelNumber || "",
        images: singleItem?.images || [],
      },
    }),
    defaultValues: emptySubProductItems,
    resolver: zodResolver(updateSubProductItemSchema),
  });

  const handleExit = (): void => {
    toggleUpdateItemModal();
    reset();
  };

  const onSubmit = async (data: TUpdateSubProductItem): Promise<void> => {
    const existingImages =
      Array.isArray(data?.images) &&
      data?.images?.filter(
        (image) => typeof image === "string" && image.startsWith("http"),
      );

    const images = data.images?.filter((image) => typeof image !== "string");
    if (!images || (Array.isArray(images) && images.length === 0)) {
      delete data.images;
    }

    const formData = parseToFormData(
      data,
      ["images"],
      form.formState.dirtyFields,
    );

    if (Array.isArray(existingImages) && existingImages?.length > 0) {
      formData.append("existingImages", JSON.stringify(existingImages));
    }

    const response = await updateSubProductItem({
      method: REQUEST_METHODS.PUT,
      data: formData,
      productId,
      subProductId,
      itemId,
    });

    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(ToolsConstants.FORM.ITEMS.UPDATED_ITEMS_SUCCESS);
        handleExit();
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      title={ToolsConstants.FORM.ITEMS.UPDATE_ITEM}
      toggleModal={handleExit}
      isLoading={isItemFetching}
      className={"px-2"}
      isFormDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          <div className='space-y-4 p-2'>
            <FormInput
              form={form}
              name={"name"}
              label='Name'
              required
              input={{
                placeholder: "Enter Product Name",
              }}
            />
            <FormInput
              form={form}
              name={"images"}
              render={(field, _, formState) => {
                return (
                  <FileUploaderComponent
                    field={field}
                    formState={formState}
                    isMultiple={true}
                    maxFilePreview={4}
                    labelsProps={{
                      label: "Upload Image",
                      showLabel: true,
                    }}
                    {...field}
                  />
                );
              }}
            />
            <FormInput
              form={form}
              name={"modelNumber"}
              label='Model Number'
              required
              input={{
                placeholder: "Enter Model Number",
                type: "number",
              }}
            />
          </div>
          <div className={"flex justify-between"}>
            <Button variant={"ghost"} onClick={() => form.reset()}>
              {Button_Names.Discard_Change}
            </Button>
            <Button
              type='submit'
              className='w-[200px]'
              isLoading={isLoading}
              disabled={!form.formState.isDirty}
            >
              {Button_Names.Save_Changes}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default UpdateItemModal;
